import {
  AGWASuiteApiClientBase,
  ApiResponse,
} from '../../../api/agwasuite-api';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import type {
  AddBushfireReportProductLinkInput,
  AppendBushfireAuditNoteInput,
  BushfireLinkedProductDto,
  BushfireReportAuditEntryDto,
  BushfireReportDetailDto,
  BushfireReportListResult,
  BushfireReportOperationResult,
  BushfireReportSummaryDto,
  BushfireReportTransitionInput,
  CreateBushfireReportInput,
  ListBushfireReportsInput,
  RemoveBushfireReportProductLinkInput,
  UpdateBushfireReportInput,
  AppendAS2047AuditNoteInput,
  AppendProductAuditNoteInput,
  AS2047ComponentDetailDto,
  AS2047ReportAuditEntryDto,
  AS2047ReportDetailDto,
  AS2047ReportListResult,
  AS2047ReportOperationResult,
  AS2047ReportSummaryDto,
  AS2047ReportTransitionInput,
  AS2047TestReportNumberDuplicateDto,
  AS2047TestReportNumberDuplicatesResponse,
  FindAS2047TestReportNumberDuplicatesInput,
  AS5218ReportAuditEntryDto,
  AS5218ReportDetailDto,
  AS5218ReportListResult,
  AS5218ReportOperationResult,
  AS5218ReportSummaryDto,
  AS5218ReportTransitionInput,
  AppendAS5218AuditNoteInput,
  BaseThermBatchAuditEntryDto,
  BaseThermBatchDetailDto,
  BaseThermBatchOperationResult,
  BaseThermBatchSummaryDto,
  BaseThermFrameSlotDto,
  CancelReportReviewInput,
  RetireProductInput,
  UnretireProductInput,
  ActivateProductInput,
  RestoreProductInput,
  CreateAS2047ComponentDetailInput,
  CreateAS2047ReportInput,
  CreateAS5218ReportInput,
  CreateBaseThermBatchInput,
  CreateDeflectionRatioInput,
  CreateFrameMaterialInput,
  CreateGlazingOptionInput,
  CreateHardwareTypeInput,
  CreateOperatingTypeInput,
  CreateProductBrandingInput,
  CreateProductInput,
  CreateProductSeriesRangeInput,
  CreateTestLaboratoryInput,
  CreateWERSLinkInput,
  ListAS2047ReportsInput,
  ListAS5218ReportsInput,
  ListBaseThermBatchesInput,
  ListProductsInput,
  ListWERSLinksInput,
  ProductAuditLogDto,
  ProductBrandingDto,
  ProductBrandingOperationResult,
  ProductDetailDto,
  ProductEnergyProfileDto,
  ProductListResult,
  ProductOperationResult,
  ProductRegistryAccessDto,
  ProductRegistryDeleteOperationResult,
  HardwareTypeOperationResult,
  ProductRegistryLookupHistoryResult,
  ProductRegistryLookupOperationResult,
  ProductRegistryLookupsDto,
  ProductSeriesRangeDto,
  ProductReportFilterHints,
  ProductReportListResult,
  ProductReportOwnerHint,
  ProductReportSearchRequest,
  ProductReportStateHint,
  ProductReportSummaryDto,
  ProductReportTypeHint,
  ProductSeriesRangeOperationResult,
  ProductSummaryDto,
  ProductStatus,
  ProductState,
  SetCurrentProductBrandingInput,
  StartReportReviewInput,
  SoftDeleteBaseThermBatchInput,
  SoftDeleteProductInput,
  SoftDeleteProductSeriesRangeInput,
  SoftDeleteWERSLinkInput,
  UpdateAS2047ComponentDetailInput,
  UpdateAS2047ReportInput,
  UpdateAS5218ReportInput,
  UpdateBaseThermBatchInput,
  UpdateDeflectionRatioInput,
  UpdateFrameMaterialInput,
  UpdateGlazingOptionInput,
  UpdateHardwareTypeInput,
  UpdateOperatingTypeInput,
  UpdateProductBrandingInput,
  UpdateProductInput,
  UpdateProductSeriesRangeInput,
  UpdateTestLaboratoryInput,
  UpdateWERSLinkInput,
  WERSAuditAction,
  WERSLinkAuditEntryDto,
  WERSLinkDetailDto,
  WERSLinkOperationResult,
  WERSLinkSummaryDto,
} from '../../../api/agwa-api-model';

// UI-layer abstraction for approve / reject / complete-review actions that
// share the same payload shape but target different report-type endpoints.
// Not emitted by the server's OpenAPI spec directly.
export interface ReportAuditorTransitionInput {
  reportId: string;
  reportType: string;
  concurrencyToken: string;
  notes?: string;
  /**
   * Structured per-field rejection items. Sent only on reject; the server
   * persists them to the shared rejection log. Empty / omitted = note-only
   * rejection. Ignored by approve.
   */
  rejectionItems?: ReportRejectionItemPayload[];
}

/** A single flagged field sent with a reject. Mirrors ProductReportRejectionItemInput. */
export interface ReportRejectionItemPayload {
  fieldKey: string;
  label: string;
  value?: string;
  reason: string;
}

const BASE_PATH = '/api/product-registry';

export class ProductRegistryApi extends AGWASuiteApiClientBase {
  /**
   * POST one of the creates the server answers by key (D-148, connection-health rule 10): product,
   * branding, the AS 2047 / AS 5218 / Bushfire report creates, and the audit-note appends.
   *
   * The key is the page's, one per intent (RequestIntentKey), and travels as the Idempotency-Key
   * header. PASSING IT TURNS THE CLIENT'S OWN RETRY ON for this request: without a key an unsafe
   * request is held to one attempt (resolveRetryOptions), and with one it gets the default attempts
   * (three) on a network failure or a retryable status. That is intended, and it is safe only
   * because the server honours the key - a retry of a create that landed is answered with the
   * first result instead of making a second record. The two ship together; a key sent to an
   * endpoint whose server does not honour it would make every retry a possible duplicate.
   *
   * A keyed request also asks again, with the same key, while the server answers it "still
   * working" because another copy of it is still being carried out (#398 C6,
   * requestUntilNotStillWorking), and gives up within a bound as a failure marked `inProgress`,
   * which the page's RequestIntentKey keeps its key through.
   */
  private postKeyed<TResult>(
    path: string,
    input: unknown,
    idempotencyKey: string
  ): Promise<ApiResponse<TResult>> {
    return this.request<TResult>('POST', path, input, { idempotencyKey });
  }

  /**
   * The caller's registers and roles. `includeInactiveOwners` asks for inactive companies'
   * registers too, marked `isActive: false`; the server honours it for AGWA staff only (D-146).
   * Only a list scoped to one company asks, so staff can open an inactive company's register
   * from its page. The menu and every other caller keep the active-only default.
   */
  async getAccess(
    includeInactiveOwners = false
  ): Promise<ApiResponse<ProductRegistryAccessDto>> {
    const query = includeInactiveOwners ? '?includeInactiveOwners=true' : '';
    return this.request<ProductRegistryAccessDto>(
      'GET',
      `${BASE_PATH}/access${query}`
    );
  }

  async getSupportData(): Promise<ApiResponse<ProductRegistryLookupsDto>> {
    return this.request<ProductRegistryLookupsDto>(
      'GET',
      `${BASE_PATH}/support-data`
    );
  }

  async listProducts(
    input: ListProductsInput
  ): Promise<ApiResponse<ProductListResult>> {
    return this.postExecute<ListProductsInput, ProductListResult>(
      `${BASE_PATH}/products/list`,
      input
    );
  }

  async getProduct(
    productId: string,
    includeAuditLog = false
  ): Promise<ApiResponse<ProductDetailDto>> {
    return this.request<ProductDetailDto>(
      'GET',
      `${BASE_PATH}/products/${productId}?includeAuditLog=${includeAuditLog}`
    );
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async createProduct(
    input: CreateProductInput,
    idempotencyKey: string
  ): Promise<ApiResponse<ProductOperationResult>> {
    return this.postKeyed<ProductOperationResult>(
      `${BASE_PATH}/products`,
      input,
      idempotencyKey
    );
  }

  async updateProduct(
    input: UpdateProductInput
  ): Promise<ApiResponse<ProductOperationResult>> {
    return this.request<ProductOperationResult>(
      'PUT',
      `${BASE_PATH}/products/${input.productId}`,
      input
    );
  }

  async deleteProduct(
    input: SoftDeleteProductInput
  ): Promise<ApiResponse<ProductRegistryDeleteOperationResult>> {
    return this.request<ProductRegistryDeleteOperationResult>(
      'DELETE',
      `${BASE_PATH}/products/${input.productId}`,
      input
    );
  }

  /**
   * Retire a product, taking it off the default register listing without deleting it.
   * Auditor or creator (D-088); the reason is the optional note recorded on the audit row.
   */
  async retireProduct(
    input: RetireProductInput
  ): Promise<ApiResponse<ProductOperationResult>> {
    return this.request<ProductOperationResult>(
      'POST',
      `${BASE_PATH}/products/${input.productId}/retire`,
      input
    );
  }

  /**
   * Return a retired product to Active - the only un-retire destination (mistake
   * recovery, D-088). Auditor-only; the compliance signal is untouched.
   */
  async unretireProduct(
    input: UnretireProductInput
  ): Promise<ApiResponse<ProductOperationResult>> {
    return this.request<ProductOperationResult>(
      'POST',
      `${BASE_PATH}/products/${input.productId}/unretire`,
      input
    );
  }

  /**
   * Activate a Submitted product - the auditor's manual act that puts it on the
   * register as Active, never automatic on report approval (D-088).
   */
  async activateProduct(
    input: ActivateProductInput
  ): Promise<ApiResponse<ProductOperationResult>> {
    return this.request<ProductOperationResult>(
      'POST',
      `${BASE_PATH}/products/${input.productId}/activate`,
      input
    );
  }

  /**
   * Restore a soft-deleted product to Submitted - the reverse of deleteProduct,
   * reachable from the deleted view (D-088).
   */
  async restoreProduct(
    input: RestoreProductInput
  ): Promise<ApiResponse<ProductOperationResult>> {
    return this.request<ProductOperationResult>(
      'POST',
      `${BASE_PATH}/products/${input.productId}/restore`,
      input
    );
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async appendAuditNote(
    input: AppendProductAuditNoteInput,
    idempotencyKey: string
  ): Promise<ApiResponse<ProductAuditLogDto>> {
    return this.postKeyed<ProductAuditLogDto>(
      `${BASE_PATH}/products/${input.productId}/notes`,
      input,
      idempotencyKey
    );
  }

  async getBrandingHistory(
    productId: string
  ): Promise<ApiResponse<ProductBrandingDto[]>> {
    return this.request<ProductBrandingDto[]>(
      'GET',
      `${BASE_PATH}/products/${productId}/brandings`
    );
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async createBranding(
    input: CreateProductBrandingInput,
    idempotencyKey: string
  ): Promise<ApiResponse<ProductBrandingOperationResult>> {
    return this.postKeyed<ProductBrandingOperationResult>(
      `${BASE_PATH}/products/${input.productId}/brandings`,
      input,
      idempotencyKey
    );
  }

  async updateBranding(
    input: UpdateProductBrandingInput
  ): Promise<ApiResponse<ProductBrandingOperationResult>> {
    return this.request<ProductBrandingOperationResult>(
      'PUT',
      `${BASE_PATH}/products/${input.productId}/brandings/${input.brandingId}`,
      input
    );
  }

  async setCurrentBranding(
    input: SetCurrentProductBrandingInput
  ): Promise<ApiResponse<ProductOperationResult>> {
    return this.postExecute<
      SetCurrentProductBrandingInput,
      ProductOperationResult
    >(`${BASE_PATH}/products/${input.productId}/set-current-branding`, input);
  }

  async listSeriesRanges(
    input: ListProductSeriesRangesInput
  ): Promise<ApiResponse<ProductSeriesRangeDto[]>> {
    return this.request<ProductSeriesRangeDto[]>(
      'GET',
      `${BASE_PATH}/owners/${input.productRegistryOwnerId}/series-ranges?includeInactive=${input.includeInactive}`
    );
  }

  async createSeriesRange(
    input: CreateProductSeriesRangeInput
  ): Promise<ApiResponse<ProductSeriesRangeOperationResult>> {
    return this.postExecute<
      CreateProductSeriesRangeInput,
      ProductSeriesRangeOperationResult
    >(
      `${BASE_PATH}/owners/${input.productRegistryOwnerId}/series-ranges`,
      input
    );
  }

  async updateSeriesRange(
    input: UpdateProductSeriesRangeInput
  ): Promise<ApiResponse<ProductSeriesRangeOperationResult>> {
    return this.request<ProductSeriesRangeOperationResult>(
      'PUT',
      `${BASE_PATH}/owners/${input.productRegistryOwnerId}/series-ranges/${input.seriesRangeId}`,
      input
    );
  }

  async deleteSeriesRange(
    input: SoftDeleteProductSeriesRangeInput
  ): Promise<ApiResponse<ProductRegistryDeleteOperationResult>> {
    return this.request<ProductRegistryDeleteOperationResult>(
      'DELETE',
      `${BASE_PATH}/owners/${input.productRegistryOwnerId}/series-ranges/${input.seriesRangeId}`,
      input
    );
  }

  /**
   * A series / range's history, newest first, for its Edit dialog (SET-LOOKUP-005 to 007). Read
   * through the register it belongs to; the server refuses anyone who may not manage that register.
   */
  async getSeriesRangeHistory(
    ownerId: string,
    seriesRangeId: string
  ): Promise<ApiResponse<ProductRegistryLookupHistoryResult>> {
    return this.request<ProductRegistryLookupHistoryResult>(
      'GET',
      `${BASE_PATH}/owners/${ownerId}/series-ranges/${seriesRangeId}/history`
    );
  }

  /**
   * A shared catalogue row's history, newest first, for its Edit dialog. `lookupType` is one of the
   * six catalogue audit types (OperatingType, FrameMaterial, TestLaboratory, GlazingOption,
   * HardwareType, DeflectionRatio); the server refuses anyone who may not maintain the catalogues.
   */
  async getLookupHistory(
    lookupType: string,
    lookupId: string
  ): Promise<ApiResponse<ProductRegistryLookupHistoryResult>> {
    return this.request<ProductRegistryLookupHistoryResult>(
      'GET',
      `${BASE_PATH}/admin/lookup-history/${encodeURIComponent(lookupType)}/${lookupId}`
    );
  }

  async createOperatingType(
    input: CreateOperatingTypeInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.postExecute<
      CreateOperatingTypeInput,
      ProductRegistryLookupOperationResult
    >(`${BASE_PATH}/admin/operating-types`, input);
  }

  async updateOperatingType(
    input: UpdateOperatingTypeInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.request<ProductRegistryLookupOperationResult>(
      'PUT',
      `${BASE_PATH}/admin/operating-types/${input.id}`,
      input
    );
  }

  async createFrameMaterial(
    input: CreateFrameMaterialInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.postExecute<
      CreateFrameMaterialInput,
      ProductRegistryLookupOperationResult
    >(`${BASE_PATH}/admin/frame-materials`, input);
  }

  async updateFrameMaterial(
    input: UpdateFrameMaterialInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.request<ProductRegistryLookupOperationResult>(
      'PUT',
      `${BASE_PATH}/admin/frame-materials/${input.id}`,
      input
    );
  }

  async createTestLaboratory(
    input: CreateTestLaboratoryInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.postExecute<
      CreateTestLaboratoryInput,
      ProductRegistryLookupOperationResult
    >(`${BASE_PATH}/admin/test-laboratories`, input);
  }

  async updateTestLaboratory(
    input: UpdateTestLaboratoryInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.request<ProductRegistryLookupOperationResult>(
      'PUT',
      `${BASE_PATH}/admin/test-laboratories/${input.id}`,
      input
    );
  }

  async createDeflectionRatio(
    input: CreateDeflectionRatioInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.postExecute<
      CreateDeflectionRatioInput,
      ProductRegistryLookupOperationResult
    >(`${BASE_PATH}/admin/deflection-ratios`, input);
  }

  async updateDeflectionRatio(
    input: UpdateDeflectionRatioInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.request<ProductRegistryLookupOperationResult>(
      'PUT',
      `${BASE_PATH}/admin/deflection-ratios/${input.id}`,
      input
    );
  }

  async createGlazingOption(
    input: CreateGlazingOptionInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.postExecute<
      CreateGlazingOptionInput,
      ProductRegistryLookupOperationResult
    >(`${BASE_PATH}/admin/glazing-options`, input);
  }

  async updateGlazingOption(
    input: UpdateGlazingOptionInput
  ): Promise<ApiResponse<ProductRegistryLookupOperationResult>> {
    return this.request<ProductRegistryLookupOperationResult>(
      'PUT',
      `${BASE_PATH}/admin/glazing-options/${input.id}`,
      input
    );
  }

  /**
   * Hardware returns its own result type rather than the shared lookup result: hardware has no
   * code, and the shared shape requires one.
   */
  async createHardwareType(
    input: CreateHardwareTypeInput
  ): Promise<ApiResponse<HardwareTypeOperationResult>> {
    return this.postExecute<
      CreateHardwareTypeInput,
      HardwareTypeOperationResult
    >(`${BASE_PATH}/admin/hardware-types`, input);
  }

  async updateHardwareType(
    input: UpdateHardwareTypeInput
  ): Promise<ApiResponse<HardwareTypeOperationResult>> {
    return this.request<HardwareTypeOperationResult>(
      'PUT',
      `${BASE_PATH}/admin/hardware-types/${input.id}`,
      input
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRODUCT REPORTS (cross-product, generic over report type)
  // ═══════════════════════════════════════════════════════════════════════════

  async listProductReports(
    input: ProductReportSearchRequest
  ): Promise<ApiResponse<ProductReportListResult>> {
    return this.postExecute<
      ProductReportSearchRequest,
      ProductReportListResult
    >(`${BASE_PATH}/reports/list`, input);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AS 2047 REPORTS
  // ═══════════════════════════════════════════════════════════════════════════

  async listAS2047Reports(
    input: ListAS2047ReportsInput
  ): Promise<ApiResponse<AS2047ReportListResult>> {
    return this.postExecute<ListAS2047ReportsInput, AS2047ReportListResult>(
      `${BASE_PATH}/as2047/list`,
      input
    );
  }

  async getAS2047Report(
    reportId: string,
    includeAuditHistory = false
  ): Promise<ApiResponse<AS2047ReportDetailDto>> {
    const path = `${BASE_PATH}/as2047/${reportId}${
      includeAuditHistory ? '?includeAuditHistory=true' : ''
    }`;
    return this.request<AS2047ReportDetailDto>('GET', path);
  }

  /**
   * The AS 2047 reports that already carry a test report number (D-145), for the new report
   * page's warning. The server matches trimmed and ignoring case, answers an empty list for a
   * blank number, and marks each row with whether this caller may open it.
   */
  async findAS2047TestReportNumberDuplicates(
    input: FindAS2047TestReportNumberDuplicatesInput
  ): Promise<ApiResponse<AS2047TestReportNumberDuplicatesResponse>> {
    return this.postExecute<
      FindAS2047TestReportNumberDuplicatesInput,
      AS2047TestReportNumberDuplicatesResponse
    >(`${BASE_PATH}/as2047/test-report-number-duplicates`, input);
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async createAS2047Report(
    input: CreateAS2047ReportInput,
    idempotencyKey: string
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postKeyed<AS2047ReportOperationResult>(
      `${BASE_PATH}/as2047/create`,
      input,
      idempotencyKey
    );
  }

  async updateAS2047Report(
    input: UpdateAS2047ReportInput
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      UpdateAS2047ReportInput,
      AS2047ReportOperationResult
    >(`${BASE_PATH}/as2047/${input.reportId}/update`, input);
  }

  async submitAS2047Report(
    input: AS2047ReportTransitionInput
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      AS2047ReportTransitionInput,
      AS2047ReportOperationResult
    >(`${BASE_PATH}/as2047/${input.reportId}/submit`, input);
  }

  async deleteAS2047Report(
    input: AS2047ReportTransitionInput
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      AS2047ReportTransitionInput,
      AS2047ReportOperationResult
    >(`${BASE_PATH}/as2047/${input.reportId}/delete`, input);
  }

  /**
   * Dual-role single-shot: collapses Submit + StartReview + Approve into one
   * transition. Only succeeds when the caller holds both creator and auditor
   * roles for the report's owner; enforced server-side (the button is gated by
   * the report's canApproveImmediately flag).
   */
  async approveImmediatelyAS2047Report(
    input: AS2047ReportTransitionInput
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      AS2047ReportTransitionInput,
      AS2047ReportOperationResult
    >(`${BASE_PATH}/as2047/${input.reportId}/approve-immediately`, input);
  }

  // No saveAS2047Configuration: the drawing is carried on updateAS2047Report
  // so it commits with the report it belongs to, in one transaction and one
  // audit trail, instead of persisting while an edit is still in progress.

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async appendAS2047AuditNote(
    input: AppendAS2047AuditNoteInput,
    idempotencyKey: string
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postKeyed<AS2047ReportOperationResult>(
      `${BASE_PATH}/as2047/${input.reportId}/audit-note`,
      input,
      idempotencyKey
    );
  }

  async createAS2047ComponentDetail(
    input: CreateAS2047ComponentDetailInput
  ): Promise<ApiResponse<AS2047ComponentDetailDto>> {
    return this.postExecute<
      CreateAS2047ComponentDetailInput,
      AS2047ComponentDetailDto
    >(`${BASE_PATH}/as2047/${input.aS2047ReportId}/components/create`, input);
  }

  async updateAS2047ComponentDetail(
    reportId: string,
    input: UpdateAS2047ComponentDetailInput
  ): Promise<ApiResponse<AS2047ComponentDetailDto>> {
    return this.postExecute<
      UpdateAS2047ComponentDetailInput,
      AS2047ComponentDetailDto
    >(
      `${BASE_PATH}/as2047/${reportId}/components/${input.componentDetailId}/update`,
      input
    );
  }

  async deleteAS2047ComponentDetail(
    reportId: string,
    componentDetailId: string
  ): Promise<ApiResponse<void>> {
    // No sessionId query parameter. The server verifies the caller holds the review lock using the
    // PROVED tab session from the X-AGWA-Tab-Session header; the query value it used to bind was
    // never read by the job, so sending one was a caller-supplied session with no consumer (#366).
    return this.request<void>(
      'DELETE',
      `${BASE_PATH}/as2047/${reportId}/components/${componentDetailId}`
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // AS 5218 REPORTS (acoustic test reports)
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // Mirrors the AS 2047 surface minus visual-config / component-detail —
  // AS 5218 is data-only per the server controller doc. Workflow transitions
  // (approve / reject / review/start / review/cancel) flow through the shared
  // methods below: callers pass reportType: 'AS5218'.

  async listAS5218Reports(
    input: ListAS5218ReportsInput
  ): Promise<ApiResponse<AS5218ReportListResult>> {
    return this.postExecute<ListAS5218ReportsInput, AS5218ReportListResult>(
      `${BASE_PATH}/as5218/list`,
      input
    );
  }

  async getAS5218Report(
    reportId: string,
    includeAuditHistory = false
  ): Promise<ApiResponse<AS5218ReportDetailDto>> {
    const path = `${BASE_PATH}/as5218/${reportId}${
      includeAuditHistory ? '?includeAuditHistory=true' : ''
    }`;
    return this.request<AS5218ReportDetailDto>('GET', path);
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async createAS5218Report(
    input: CreateAS5218ReportInput,
    idempotencyKey: string
  ): Promise<ApiResponse<AS5218ReportOperationResult>> {
    return this.postKeyed<AS5218ReportOperationResult>(
      `${BASE_PATH}/as5218/create`,
      input,
      idempotencyKey
    );
  }

  async updateAS5218Report(
    input: UpdateAS5218ReportInput
  ): Promise<ApiResponse<AS5218ReportOperationResult>> {
    return this.postExecute<
      UpdateAS5218ReportInput,
      AS5218ReportOperationResult
    >(`${BASE_PATH}/as5218/${input.reportId}/update`, input);
  }

  async submitAS5218Report(
    input: AS5218ReportTransitionInput
  ): Promise<ApiResponse<AS5218ReportOperationResult>> {
    return this.postExecute<
      AS5218ReportTransitionInput,
      AS5218ReportOperationResult
    >(`${BASE_PATH}/as5218/${input.reportId}/submit`, input);
  }

  async deleteAS5218Report(
    input: AS5218ReportTransitionInput
  ): Promise<ApiResponse<AS5218ReportOperationResult>> {
    return this.postExecute<
      AS5218ReportTransitionInput,
      AS5218ReportOperationResult
    >(`${BASE_PATH}/as5218/${input.reportId}/delete`, input);
  }

  /**
   * Dual-role single-shot for AS 5218 (Submit + StartReview + Approve in one).
   * Gated by the report's canApproveImmediately flag; enforced server-side.
   */
  async approveImmediatelyAS5218Report(
    input: AS5218ReportTransitionInput
  ): Promise<ApiResponse<AS5218ReportOperationResult>> {
    return this.postExecute<
      AS5218ReportTransitionInput,
      AS5218ReportOperationResult
    >(`${BASE_PATH}/as5218/${input.reportId}/approve-immediately`, input);
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async appendAS5218AuditNote(
    input: AppendAS5218AuditNoteInput,
    idempotencyKey: string
  ): Promise<ApiResponse<AS5218ReportOperationResult>> {
    return this.postKeyed<AS5218ReportOperationResult>(
      `${BASE_PATH}/as5218/${input.reportId}/audit-note`,
      input,
      idempotencyKey
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BUSHFIRE (AS 1530) REPORTS — many:many (one report covers many products).
  // Auditor actions (approve / reject / review/start / review/cancel) flow
  // through the shared methods below: callers pass reportType: 'Bushfire'.
  // ═══════════════════════════════════════════════════════════════════════════

  async listBushfireReports(
    input: ListBushfireReportsInput
  ): Promise<ApiResponse<BushfireReportListResult>> {
    return this.postExecute<ListBushfireReportsInput, BushfireReportListResult>(
      `${BASE_PATH}/bushfire/list`,
      input
    );
  }

  async getBushfireReport(
    reportId: string,
    includeAuditHistory = false
  ): Promise<ApiResponse<BushfireReportDetailDto>> {
    const path = `${BASE_PATH}/bushfire/${reportId}${
      includeAuditHistory ? '?includeAuditHistory=true' : ''
    }`;
    return this.request<BushfireReportDetailDto>('GET', path);
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async createBushfireReport(
    input: CreateBushfireReportInput,
    idempotencyKey: string
  ): Promise<ApiResponse<BushfireReportOperationResult>> {
    return this.postKeyed<BushfireReportOperationResult>(
      `${BASE_PATH}/bushfire/create`,
      input,
      idempotencyKey
    );
  }

  async updateBushfireReport(
    input: UpdateBushfireReportInput
  ): Promise<ApiResponse<BushfireReportOperationResult>> {
    return this.postExecute<
      UpdateBushfireReportInput,
      BushfireReportOperationResult
    >(`${BASE_PATH}/bushfire/${input.reportId}/update`, input);
  }

  async submitBushfireReport(
    input: BushfireReportTransitionInput
  ): Promise<ApiResponse<BushfireReportOperationResult>> {
    return this.postExecute<
      BushfireReportTransitionInput,
      BushfireReportOperationResult
    >(`${BASE_PATH}/bushfire/${input.reportId}/submit`, input);
  }

  async deleteBushfireReport(
    input: BushfireReportTransitionInput
  ): Promise<ApiResponse<BushfireReportOperationResult>> {
    return this.postExecute<
      BushfireReportTransitionInput,
      BushfireReportOperationResult
    >(`${BASE_PATH}/bushfire/${input.reportId}/delete`, input);
  }

  /** A keyed create (D-148): `idempotencyKey` is the page's key for this intent; see postKeyed. */
  async appendBushfireAuditNote(
    input: AppendBushfireAuditNoteInput,
    idempotencyKey: string
  ): Promise<ApiResponse<BushfireReportOperationResult>> {
    return this.postKeyed<BushfireReportOperationResult>(
      `${BASE_PATH}/bushfire/${input.reportId}/audit-note`,
      input,
      idempotencyKey
    );
  }

  async addBushfireReportProductLink(
    input: AddBushfireReportProductLinkInput
  ): Promise<ApiResponse<BushfireReportOperationResult>> {
    return this.postExecute<
      AddBushfireReportProductLinkInput,
      BushfireReportOperationResult
    >(`${BASE_PATH}/bushfire/${input.reportId}/products/add`, input);
  }

  async removeBushfireReportProductLink(
    input: RemoveBushfireReportProductLinkInput
  ): Promise<ApiResponse<BushfireReportOperationResult>> {
    return this.postExecute<
      RemoveBushfireReportProductLinkInput,
      BushfireReportOperationResult
    >(`${BASE_PATH}/bushfire/${input.reportId}/products/remove`, input);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORT AUDITOR ACTIONS (generic over reportType: AS2047, AS5218, WERS, …)
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // History: Phase 4 (commit a7d2dc1) deleted the cross-cutting
  // ProductRegistryAuditorController in favour of per-type controllers
  // ("per-type controller, fully typed, no string discriminator"). The UI
  // client kept the generic /auditor/{reportType}/... URL shape but those
  // routes no longer exist on the server, so every call from this section
  // returned 404. These methods now route to the type-specific endpoints
  // that actually exist (e.g. /api/product-registry/as2047/{id}/approve)
  // via _reportTypeSegment, preserving the single client surface while
  // matching the actual server routing.

  /**
   * Map a logical reportType ("AS2047", "AS5218", "WERS", …) to its URL
   * segment on the per-type controller route ("as2047", "as5218", "wers").
   * Server routes are lowercase; the reportType constants used in client
   * code are upper-case. Single helper so any future deviation (e.g. an
   * alias) has one place to live.
   */
  private _reportTypeSegment(reportType: string): string {
    return reportType.toLowerCase();
  }

  async approveReport(
    input: ReportAuditorTransitionInput
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      ReportAuditorTransitionInput,
      AS2047ReportOperationResult
    >(
      `${BASE_PATH}/${this._reportTypeSegment(input.reportType)}/${input.reportId}/approve`,
      input
    );
  }

  async rejectReport(
    input: ReportAuditorTransitionInput
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      ReportAuditorTransitionInput,
      AS2047ReportOperationResult
    >(
      `${BASE_PATH}/${this._reportTypeSegment(input.reportType)}/${input.reportId}/reject`,
      input
    );
  }

  /**
   * Mark an Approved report Superseded - the alias state of Approved (D-088): it
   * still counts as approved everywhere approval matters, while filters present it
   * separately (purple). Auditor-only; only Approved reports supersede.
   */
  async supersedeReport(
    input: ReportAuditorTransitionInput
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      ReportAuditorTransitionInput,
      AS2047ReportOperationResult
    >(
      `${BASE_PATH}/${this._reportTypeSegment(input.reportType)}/${input.reportId}/supersede`,
      input
    );
  }

  async startReportReview(
    input: StartReportReviewInput & { reportType: string }
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      StartReportReviewInput,
      AS2047ReportOperationResult
    >(
      `${BASE_PATH}/${this._reportTypeSegment(input.reportType)}/${input.reportId}/review/start`,
      input
    );
  }

  async cancelReportReview(
    input: CancelReportReviewInput & { reportType: string }
  ): Promise<ApiResponse<AS2047ReportOperationResult>> {
    return this.postExecute<
      CancelReportReviewInput,
      AS2047ReportOperationResult
    >(
      `${BASE_PATH}/${this._reportTypeSegment(input.reportType)}/${input.reportId}/review/cancel`,
      input
    );
  }

  // ─── WERS Integration (Phase 3) ────────────────────────────────────────────
  //
  // Every path below states the whole chain it addresses, down from the product, because that
  // is what the server checks the row against. The client has always known the chain - the
  // routes it is rendered under are products/:productId/wers/:wersLinkId/batches/:batchId - it
  // simply used not to send it.

  async getProductEnergyProfile(
    productId: string
  ): Promise<ApiResponse<ProductEnergyProfileDto>> {
    return this.request<ProductEnergyProfileDto>(
      'GET',
      `${BASE_PATH}/wers/products/${productId}/profile`
    );
  }

  async listWERSLinks(
    input: ListWERSLinksInput
  ): Promise<ApiResponse<WERSLinkSummaryDto[]>> {
    return this.postExecute<ListWERSLinksInput, WERSLinkSummaryDto[]>(
      `${BASE_PATH}/wers/products/${input.productId}/links/list`,
      input
    );
  }

  async getWERSLink(
    productId: string,
    linkId: string,
    includeAuditHistory = false
  ): Promise<ApiResponse<WERSLinkDetailDto>> {
    const path = `${BASE_PATH}/wers/products/${productId}/links/${linkId}${
      includeAuditHistory ? '?includeAuditHistory=true' : ''
    }`;
    return this.request<WERSLinkDetailDto>('GET', path);
  }

  async createWERSLink(
    input: CreateWERSLinkInput
  ): Promise<ApiResponse<WERSLinkOperationResult>> {
    return this.postExecute<CreateWERSLinkInput, WERSLinkOperationResult>(
      `${BASE_PATH}/wers/products/${input.productId}/links/create`,
      input
    );
  }

  async updateWERSLink(
    input: UpdateWERSLinkInput
  ): Promise<ApiResponse<WERSLinkOperationResult>> {
    return this.postExecute<UpdateWERSLinkInput, WERSLinkOperationResult>(
      `${BASE_PATH}/wers/products/${input.productId}/links/${input.wersLinkId}/update`,
      input
    );
  }

  async softDeleteWERSLink(
    input: SoftDeleteWERSLinkInput
  ): Promise<ApiResponse<WERSLinkOperationResult>> {
    return this.postExecute<SoftDeleteWERSLinkInput, WERSLinkOperationResult>(
      `${BASE_PATH}/wers/products/${input.productId}/links/${input.wersLinkId}/delete`,
      input
    );
  }

  async listBaseThermBatches(
    input: ListBaseThermBatchesInput
  ): Promise<ApiResponse<BaseThermBatchSummaryDto[]>> {
    return this.postExecute<
      ListBaseThermBatchesInput,
      BaseThermBatchSummaryDto[]
    >(
      `${BASE_PATH}/wers/products/${input.productId}/links/${input.wersLinkId}/batches/list`,
      input
    );
  }

  async getBaseThermBatch(
    productId: string,
    wersLinkId: string,
    batchId: string,
    includeAuditHistory = false
  ): Promise<ApiResponse<BaseThermBatchDetailDto>> {
    const path = `${BASE_PATH}/wers/products/${productId}/links/${wersLinkId}/batches/${batchId}${
      includeAuditHistory ? '?includeAuditHistory=true' : ''
    }`;
    return this.request<BaseThermBatchDetailDto>('GET', path);
  }

  async createBaseThermBatch(
    input: CreateBaseThermBatchInput
  ): Promise<ApiResponse<BaseThermBatchOperationResult>> {
    return this.postExecute<
      CreateBaseThermBatchInput,
      BaseThermBatchOperationResult
    >(
      `${BASE_PATH}/wers/products/${input.productId}/links/${input.wersLinkId}/batches/create`,
      input
    );
  }

  async updateBaseThermBatch(
    input: UpdateBaseThermBatchInput
  ): Promise<ApiResponse<BaseThermBatchOperationResult>> {
    return this.postExecute<
      UpdateBaseThermBatchInput,
      BaseThermBatchOperationResult
    >(
      `${BASE_PATH}/wers/products/${input.productId}/links/${input.wersLinkId}/batches/${input.baseThermBatchId}/update`,
      input
    );
  }

  async softDeleteBaseThermBatch(
    input: SoftDeleteBaseThermBatchInput
  ): Promise<ApiResponse<BaseThermBatchOperationResult>> {
    return this.postExecute<
      SoftDeleteBaseThermBatchInput,
      BaseThermBatchOperationResult
    >(
      `${BASE_PATH}/wers/products/${input.productId}/links/${input.wersLinkId}/batches/${input.baseThermBatchId}/delete`,
      input
    );
  }
}

export function getProductRegistryApi(): ProductRegistryApi {
  return ApiFactory.authenticated(ProductRegistryApi);
}

export type {
  AddBushfireReportProductLinkInput,
  AppendBushfireAuditNoteInput,
  BushfireLinkedProductDto,
  BushfireReportAuditEntryDto,
  BushfireReportDetailDto,
  BushfireReportListResult,
  BushfireReportOperationResult,
  BushfireReportSummaryDto,
  BushfireReportTransitionInput,
  CreateBushfireReportInput,
  ListBushfireReportsInput,
  RemoveBushfireReportProductLinkInput,
  UpdateBushfireReportInput,
  AppendAS2047AuditNoteInput,
  AS2047ComponentDetailDto,
  AS2047ReportAuditEntryDto,
  AS2047ReportDetailDto,
  AS2047ReportListResult,
  AS2047ReportOperationResult,
  AS2047ReportSummaryDto,
  AS2047ReportTransitionInput,
  AS2047TestReportNumberDuplicateDto,
  AS2047TestReportNumberDuplicatesResponse,
  FindAS2047TestReportNumberDuplicatesInput,
  AS5218ReportAuditEntryDto,
  AS5218ReportDetailDto,
  AS5218ReportListResult,
  AS5218ReportOperationResult,
  AS5218ReportSummaryDto,
  AS5218ReportTransitionInput,
  AppendAS5218AuditNoteInput,
  BaseThermBatchAuditEntryDto,
  BaseThermBatchDetailDto,
  BaseThermBatchOperationResult,
  BaseThermBatchSummaryDto,
  BaseThermFrameSlotDto,
  CancelReportReviewInput,
  RetireProductInput,
  UnretireProductInput,
  ActivateProductInput,
  RestoreProductInput,
  CreateAS2047ComponentDetailInput,
  CreateAS2047ReportInput,
  CreateAS5218ReportInput,
  CreateBaseThermBatchInput,
  CreateDeflectionRatioInput,
  CreateFrameMaterialInput,
  CreateGlazingOptionInput,
  CreateHardwareTypeInput,
  CreateOperatingTypeInput,
  CreateProductBrandingInput,
  CreateProductInput,
  CreateProductSeriesRangeInput,
  CreateTestLaboratoryInput,
  CreateWERSLinkInput,
  ListAS2047ReportsInput,
  ListAS5218ReportsInput,
  ListBaseThermBatchesInput,
  ListProductsInput,
  ListWERSLinksInput,
  ProductAuditLogDto,
  ProductBrandingDto,
  ProductDetailDto,
  ProductEnergyProfileDto,
  ProductListResult,
  ProductRegistryAccessDto,
  ProductRegistryDeleteOperationResult,
  ProductRegistryLookupOperationResult,
  ProductRegistryLookupsDto,
  ProductReportFilterHints,
  ProductReportListResult,
  ProductReportOwnerHint,
  ProductReportSearchRequest,
  ProductReportStateHint,
  ProductReportSummaryDto,
  ProductReportTypeHint,
  ProductSeriesRangeDto,
  ProductSummaryDto,
  ProductStatus,
  ProductState,
  SetCurrentProductBrandingInput,
  StartReportReviewInput,
  SoftDeleteBaseThermBatchInput,
  SoftDeleteProductInput,
  SoftDeleteProductSeriesRangeInput,
  SoftDeleteWERSLinkInput,
  UpdateAS2047ComponentDetailInput,
  UpdateAS2047ReportInput,
  UpdateAS5218ReportInput,
  UpdateBaseThermBatchInput,
  UpdateDeflectionRatioInput,
  UpdateFrameMaterialInput,
  UpdateGlazingOptionInput,
  UpdateHardwareTypeInput,
  UpdateOperatingTypeInput,
  UpdateProductBrandingInput,
  UpdateProductInput,
  UpdateProductSeriesRangeInput,
  UpdateTestLaboratoryInput,
  UpdateWERSLinkInput,
  WERSAuditAction,
  WERSLinkAuditEntryDto,
  WERSLinkDetailDto,
  WERSLinkOperationResult,
  WERSLinkSummaryDto,
};

export interface ListProductSeriesRangesInput {
  productRegistryOwnerId: string;
  includeInactive?: boolean;
}
